import { RowState, mapServerErrors } from './dataset.js';
import { setHtml } from './css.js';
import { applyConflict, conflictAction } from './spring.js';
import { crossRowErrors } from './rowcheck.js';
import { NIR, trapTab, nextDlgSeq } from './grid-util.js';
import { normValidation } from './input.js';
export class GridRows {
    /**
     * 새 행 추가 후 첫 편집 셀로 이동. 위치는 insertPosition(기본 끝, before·after = 포커스 행 앞·뒤 — Insert·메뉴·API 만; Tab·↓ 새 행은 끝).
     * onBeforeRowInsert 가 false 면 -1. source = 이벤트에 줄 경로
     */
    addRow(values, focusEdit = true, source = 'api') {
        this.commitEdit();
        if (this.focus.vi === NIR && !this.leaveNewItemRow())
            return -1;
        if (this.rowEdit && !this.saveRowEdit())
            return -1;
        if (this.protectBlocks('insert'))
            return -1;
        const pos = source === 'tab' || source === 'down' ? 'end' : this.opts.insertPosition ?? 'end';
        const fr = this.getFocusedRow(), fvi = fr >= 0 && pos !== 'end' && !this.isGrouped() && !this.opts.tree && !this.extOrder ? this.view.indexOf(fr) : -1;
        const at = fvi < 0 ? this.view.length : pos === 'before' ? fvi : fvi + 1;
        const vals = this.beforeRowInsert(at, { ...(this.opts.newRow?.() ?? {}), ...(values ?? {}) }, source);
        if (!vals)
            return -1;
        this.insertAt = fvi >= 0 ? at : null;
        let r;
        try {
            r = this.undoBatch(this.t('행 추가'), () => this.ds.addRow(vals));
        }
        finally {
            this.insertAt = null;
        }
        // 빈 새 행 자동 취소: 값을 주지 않고 만든 행만 (복사해서 만든 행 등은 사용자가 준 값이 있음)
        if (this.opts.cancelOnExit && r >= 0 && (!values || !Object.keys(values).length))
            this.freshRows.set(r, this.ds.getRow(r));
        if (focusEdit) {
            let vi = this.disp.lastIndexOf(r);
            if (vi < 0)
                vi = this.disp.length - 1;
            const ci = this.cols.findIndex(c => this.canEditCol(c, r));
            this.setFocus(vi, ci < 0 ? this.firstDataCol() : ci, false);
            this.ime.focus({ preventScroll: true });
        }
        return r;
    }
    /**
     * 체크된 행, 없으면 선택 범위의 행을 삭제(행 상태 D). deletingConfirmation 이 있으면 확인 창 → Promise<삭제했나>
     * (opts.confirm: false 면 묻지 않음). 확인이 필요 없으면 바로 지우고 true. 새 항목 행에서는 입력 중인 새 행을 버림
     */
    deleteRows(rows, opts = {}) {
        this.commitEdit();
        if (!rows && this.focus.vi === NIR) {
            this.cancelNewItemRow();
            return true;
        }
        if (this.protectBlocks('delete'))
            return false;
        const target = (rows ?? this.targetRows()).filter(r => r >= 0 && r < this.ds.length && this.ds.isVisibleRow(r));
        if (!target.length)
            return false;
        if (!this.beforeRowDelete(target, opts.source ?? 'api'))
            return false; // 삭제 확인 창보다 먼저
        if (this.rowEdit && target.includes(this.rowEdit.row))
            this.cancelRowEdit();
        if (this.opts.deletingConfirmation && opts.confirm !== false) {
            return this.confirmDelete(target).then(ok => { if (ok)
                this.doDeleteRows(target); this.focusGrid(); return ok; }, () => { this.focusGrid(); return false; });
        }
        this.doDeleteRows(target);
        return true;
    }
    /** 삭제 확인: 함수면 그 결과, 아니면 화면 안 확인 창 (삭제·취소, Esc = 취소) */
    /** @internal */ confirmDelete(rows) {
        const dc = this.opts.deletingConfirmation;
        if (typeof dc === 'function')
            return Promise.resolve(dc(rows, this)).then(v => !!v);
        const msg = typeof dc === 'string' ? dc.replace(/\{n\}/g, rows.length.toLocaleString('ko-KR')) : this.t('선택한 {n}건을 삭제할까요?', { n: rows.length.toLocaleString('ko-KR') });
        return this.confirmDialog(this.t('삭제 확인'), msg, [{ id: 'delete', label: this.t('삭제'), primary: true }, { id: 'cancel', label: this.t('취소') }], 'og-delc').then(id => id === 'delete');
    }
    /** @internal */ doDeleteRows(target) {
        const rec = [];
        this.ds.batch(() => { for (const r of target) {
            const before = this.ds.rowState(r);
            this.ds.deleteRow(r);
            const after = this.ds.rowState(r);
            if (after !== before)
                rec.push({ row: r, before, after });
        } });
        if (rec.length)
            this.pushUndo({ label: `행 삭제 ${rec.length}건`, at: Date.now(), cells: [], rows: rec });
        for (const r of target)
            this.checked.delete(r);
    }
    /** 체크된 행(없으면 선택 범위)의 변경 취소 */
    revertRows(rows) {
        this.cancelEdit();
        const target = rows ?? this.targetRows();
        if (this.rowEdit && target.includes(this.rowEdit.row))
            this.cancelRowEdit();
        this.ds.batch(() => { for (const r of target)
            this.ds.revertRow(r); });
        for (const r of target)
            for (const m of [this.errors, this.warns])
                for (const k of [...m.keys()])
                    if (k.startsWith(r + ':'))
                        m.delete(k);
        const ts = new Set(target);
        this.clearAsync(r => ts.has(r));
    }
    /**
     * 필수값·검증 함수·바이트 제한 검사. 실패 셀을 표시하고 첫 오류로 이동. 돌려주는 것은 저장을 막는 오류(level 'error')만 —
     * 경고·안내는 칸에 표시하고 getWarnings()·validate({ all: true }) 로 (예전과 같이 빈 배열 = 저장해도 됨)
     */
    validate(opts = {}) {
        this.commitEdit();
        if (this.rowEdit) {
            const re = this.rowEdit.row;
            if (!this.saveRowEdit())
                return this.rowEditErrors(re);
        } // 행 편집 중이면 먼저 저장 (오류면 그 오류)
        this.flushNewRows();
        const out = [];
        this.errors.clear();
        this.warns.clear();
        for (let r = 0; r < this.ds.length; r++) {
            const st = this.ds.rowState(r);
            if (st !== RowState.INSERT && st !== RowState.UPDATE)
                continue;
            for (const c of this.cols) {
                if (c.special || c.def.value)
                    continue;
                const v = this.ds.get(r, c.id);
                let m = null;
                const rule = this.ruleOf(c, r);
                if ((rule?.required ?? c.def.required) && (v == null || v === ''))
                    m = { level: 'error', message: `${c.def.header ?? c.id}은(는) 필수입니다` };
                else {
                    const b = this.bytesError(c, v);
                    m = b ? { level: 'error', message: b } : c.def.validate ? normValidation(c.def.validate(v, r, this.ds)) : null;
                }
                if ((!m || m.level !== 'error') && c.def.validateAsync) {
                    const k = r + ':' + c.id;
                    const am = this.asyncPend.has(k) ? `${c.def.header ?? c.id} 확인 중입니다` : this.asyncErr.get(k);
                    if (am)
                        m = { level: 'error', message: am };
                }
                if (!m)
                    continue;
                if (m.level === 'error') {
                    out.push({ row: r, col: c.id, message: m.message, level: 'error' });
                    this.errors.set(r + ':' + c.id, m.message);
                }
                else
                    this.warns.set(r + ':' + c.id, m);
            }
        }
        // 행 사이 검증 (uniqueBy·maxSum·validateRows): 셀 오류 + 오류 목록 창 (경고 수준은 막지 않음)
        if (this.crossOn()) {
            const xs = this.runCrossRows();
            for (const e of xs)
                if ((e.level ?? 'error') === 'error' && !this.errors.has(e.row + ':' + e.col))
                    out.push({ ...e, level: 'error' });
        }
        this.bump();
        if (out.length)
            this.focusCell(out[0].row, out[0].col);
        return opts.all ? [...out, ...this.getWarnings()] : out;
    }
    /** 행 사이 검증 옵션이 있나 */
    /** @internal */ crossOn() { const m = this.opts.maxSum; return !!(this.opts.validateRows || this.opts.uniqueBy?.length || (Array.isArray(m) ? m.length : m)); }
    /** 행 사이 검증만 실행 (화면 표시 포함) → 오류 목록 */
    validateRows() { return this.crossOn() ? this.runCrossRows() : []; }
    /** 행 사이 검증 결과 (마지막 계산) — 오류(level 'error')와 경고·안내 (getRowErrors().filter(e => e.level === 'error') = 저장을 막는 것) */
    getRowErrors() {
        const sp = (k) => { const i = k.indexOf(':'); return { row: +k.slice(0, i), col: k.slice(i + 1) }; };
        return [...[...this.rowErrs].map(([k, message]) => ({ ...sp(k), message, level: 'error' })), ...[...this.rowWarns].map(([k, w]) => ({ ...sp(k), message: w.message, level: w.level }))];
    }
    /** @internal */ runCrossRows() {
        clearTimeout(this.rowValTimer);
        this.rowValTimer = 0;
        let xs = [];
        try {
            xs = crossRowErrors(this.ds, this.ds.allRows(), { uniqueBy: this.opts.uniqueBy, uniqueByLevel: this.opts.uniqueByLevel, maxSum: this.opts.maxSum, validateRows: this.opts.validateRows, header: id => this.headerOf(id) });
        }
        catch (err) {
            console.error(err);
        }
        const before = this.rowErrs.size + this.rowWarns.size;
        const isErr = (e) => (e.level ?? 'error') === 'error';
        this.rowErrs = new Map(xs.filter(isErr).map(e => [e.row + ':' + e.col, e.message]));
        this.rowWarns = new Map(xs.filter(e => !isErr(e)).map(e => [e.row + ':' + e.col, { level: e.level, message: e.message }]));
        if (xs.length)
            this.renderErrPanel(xs.map(e => ({ ...e })), 'rows');
        else if (this.errPanelKind === 'rows') {
            this.errPanel.hidden = true;
            this.errPanelKind = null;
        }
        if (before || xs.length)
            this.bump();
        return xs.map(e => ({ ...e, level: e.level ?? 'error' }));
    }
    /** 편집 뒤 행 사이 검증 다시 (디바운스) */
    /** @internal */ schedCrossRows() {
        if (!this.crossOn()) {
            if (this.rowErrs.size || this.rowWarns.size) {
                this.rowErrs.clear();
                this.rowWarns.clear();
                if (this.errPanelKind === 'rows') {
                    this.errPanel.hidden = true;
                    this.errPanelKind = null;
                }
            }
            return;
        }
        clearTimeout(this.rowValTimer);
        this.rowValTimer = setTimeout(() => { this.rowValTimer = 0; if (this.root.isConnected)
            this.runCrossRows(); }, Math.max(0, this.opts.validateRowsDelay ?? 250));
    }
    // ── 서버 변경 있음 (실시간 갱신 충돌) ──
    /** 행에 "서버 변경 있음" 표시 (null 이면 지움). subscribeRows/applyRowEvent 가 내 편집이 있는 행에 씀 */
    markServerChanged(row, message) {
        if (message == null) {
            if (!this.srvChanged.delete(row))
                return;
        }
        else
            this.srvChanged.set(row, message);
        this.bump();
    }
    getServerChangedRows() { return [...this.srvChanged].map(([row, message]) => ({ row, message })); }
    /**
     * 저장 전 검증: 편집 확정 → 아직 검사 안 한 변경 셀의 비동기 검증 실행 → 진행 중 검증 대기 →
     * 필수·validate·validateAsync 오류 모두. 빈 배열이면 저장해도 됨.
     */
    async validateAll() {
        this.commitEdit();
        this.flushNewRows();
        for (let r = 0; r < this.ds.length; r++) {
            const st = this.ds.rowState(r);
            if (st !== RowState.INSERT && st !== RowState.UPDATE)
                continue;
            for (const c of this.cols) {
                if (!c.def.validateAsync)
                    continue;
                const k = r + ':' + c.id, v = this.ds.get(r, c.id);
                if (this.asyncPend.has(k) || (this.asyncVal.has(k) && this.asyncVal.get(k) === v))
                    continue;
                if (st === RowState.UPDATE && !this.ds.isCellChanged(r, c.id))
                    continue;
                this.scheduleAsync(r, c.id, 0);
            }
        }
        await this.whenValidated();
        const errs = this.validate();
        // 경고 저장 전 확인 (warningConfirm): 취소하면 경고 목록을 돌려줘 저장을 막음
        if (!errs.length && this.opts.warningConfirm) {
            const ws = this.getWarnings('warning');
            if (ws.length && !(await this.confirmWarnings(ws)))
                return ws;
        }
        return errs;
    }
    /** 진행 중인 비동기 검증이 모두 끝날 때까지 기다린다 (디바운스 대기 중인 것은 바로 실행) */
    whenValidated() {
        for (const e of this.asyncPend.values())
            if (e.timer) {
                clearTimeout(e.timer);
                e.timer = null;
                e.run();
            }
        if (!this.asyncPend.size)
            return Promise.resolve();
        return new Promise(res => this.asyncWaiters.push(res));
    }
    /** 비동기 검증 진행 중 여부 (row 를 주면 그 행, col 까지 주면 그 셀) */
    isValidating(row, col) {
        if (row == null)
            return this.asyncPend.size > 0;
        if (col != null)
            return this.asyncPend.has(row + ':' + col);
        for (const k of this.asyncPend.keys())
            if (k.startsWith(row + ':'))
                return true;
        return false;
    }
    /** 셀 검증 메시지 (동기 검증·서버 오류·비동기 검증·행 사이). 오류가 먼저, 없으면 경고·안내 — 수준은 getCellLevel·getCellMessage */
    getCellError(row, col) { const k = row + ':' + col; return this.errors.get(k) ?? this.asyncErr.get(k) ?? this.rowErrs.get(k) ?? this.cellWarn(row, col)?.message ?? null; }
    /** @internal */ scheduleAsync(row, col, delay = this.opts.validateAsyncDelay ?? 300) {
        const fn = this.cols.find(c => c.id === col && !c.special)?.def.validateAsync;
        if (!fn)
            return;
        const key = row + ':' + col;
        this.cancelAsync(key, false);
        this.asyncErr.delete(key);
        this.asyncWarn.delete(key);
        const token = ++this.asyncSeq, ac = new AbortController();
        const ent = { token, timer: null, ac, run: () => { } };
        ent.run = () => {
            ent.timer = null;
            const v = this.ds.get(row, col);
            let p;
            try {
                p = Promise.resolve(fn(v, row, this.ds, { signal: ac.signal, col }));
            }
            catch (err) {
                p = Promise.reject(err);
            }
            p.then(msg => this.finishAsync(key, token, v, msg ?? null), err => { if (!ac.signal.aborted)
                this.finishAsync(key, token, v, this.t('확인하지 못했습니다: ') + (err?.message ?? err)); });
        };
        ent.timer = setTimeout(ent.run, Math.max(0, delay));
        this.asyncPend.set(key, ent);
        this.bump();
    }
    /** @internal */ cancelAsync(key, settle = true) {
        const e = this.asyncPend.get(key);
        if (!e)
            return;
        if (e.timer)
            clearTimeout(e.timer);
        e.ac.abort();
        this.asyncPend.delete(key);
        if (settle)
            this.settleAsyncWaiters();
    }
    /** 조건에 맞는 행의 비동기 검증 상태를 지움 (pred 없으면 전부) */
    /** @internal */ clearAsync(pred) {
        for (const k of [...this.asyncPend.keys()])
            if (!pred || pred(+k.split(':')[0]))
                this.cancelAsync(k, false);
        for (const m of [this.asyncErr, this.asyncVal, this.asyncWarn])
            for (const k of [...m.keys()])
                if (!pred || pred(+k.split(':')[0]))
                    m.delete(k);
        this.settleAsyncWaiters();
    }
    /** @internal */ finishAsync(key, token, v, res) {
        const cur = this.asyncPend.get(key);
        if (!cur || cur.token !== token)
            return; // 그 사이 값이 또 바뀜 → 오래된 결과는 버림
        this.asyncPend.delete(key);
        this.asyncVal.set(key, v);
        const m = normValidation(res);
        this.asyncErr.delete(key);
        this.asyncWarn.delete(key);
        if (m?.level === 'error')
            this.asyncErr.set(key, m.message);
        else if (m)
            this.asyncWarn.set(key, m);
        this.bump();
        if (m && !this.editing && this.getFocusedRow() + ':' + this.cols[this.focus.ci]?.id === key)
            this.showTip(m.message, m.level);
        this.settleAsyncWaiters();
    }
    /** @internal */ settleAsyncWaiters() {
        if (this.asyncPend.size)
            return;
        const w = this.asyncWaiters;
        this.asyncWaiters = [];
        for (const f of w)
            f();
    }
    /**
     * 서버 오류(Spring GridError: section·index·field)를 보낸 행으로 되짚어 셀에 표시하고 오류 목록 창을 띄운다.
     * 이미 물리 행으로 옮긴 RowError(saveChanges 결과의 errors)도 받는다. 목록을 누르면 그 셀로 이동.
     */
    showServerErrors(errors, rows = this.ds.lastChangeRows) {
        const list = errors.map(e => ('row' in e ? e : mapServerErrors([e], rows)[0]));
        // 서버 GridError 수준: warning·info 는 표시만 (저장 막지 않음)
        for (const e of list)
            if (e.row >= 0) {
                const k = e.row + ':' + (e.col ?? this.cols.find(c => !c.special)?.id);
                if (e.level === 'warning' || e.level === 'info')
                    this.warns.set(k, { level: e.level, message: e.message });
                else
                    this.errors.set(k, e.message);
            }
        this.renderErrPanel(list, 'server');
        this.bump();
        const first = list.find(e => e.row >= 0 && (e.level ?? 'error') === 'error') ?? list.find(e => e.row >= 0);
        if (first)
            this.focusCell(first.row, first.col ?? undefined);
        return list;
    }
    /**
     * 저장 충돌 해결 창 — 충돌한 행을 한 창에 모두 보여 준다(행마다 내 값·서버 값 비교 + 선택, "모두 내 값"/"모두 서버 값").
     * "덮어쓰기"(내 값 유지, 버전만 서버 것으로) 또는 "서버 값 받기"(행을 서버 값으로 바꾸고 NORMAL). 끝나면 남은 변경을 다시 저장하면 된다.
     * 서버에서 삭제된 행: 다시 추가 = 같은 키로 INSERT(reAddKey:'new' 면 새 키), 서버 값 받기 = 화면에서 제거.
     */
    resolveConflicts(conflicts, opts = {}) {
        const vc = opts.versionCol ?? 'ver', kc = this.ds.keyCol;
        const res = { overwrite: 0, server: 0, cancelled: false };
        const esc = (t) => String(t ?? '').replace(/[&<>"]/g, ch => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[ch]));
        const cols = this.cols.filter(c => !c.special && !c.def.value && c.id !== vc);
        const show = (c, v, row) => (v == null || v === '' ? '' : this.displayText(c, v, row));
        const many = conflicts.length > 1, nm = 'og-cf' + nextDlgSeq();
        const mineLabel = (cf) => { const a = conflictAction(cf, 'mine'); return this.t(a === 'overwrite' ? '덮어쓰기' : a === 'confirmDelete' ? '삭제 확정' : '다시 추가'); };
        return new Promise(resolve => {
            const back = document.createElement('div');
            back.className = 'og-dlgb';
            this.root.appendChild(back);
            const finish = () => { back.remove(); this.bump(); this.focusGrid(); resolve(res); };
            const items = conflicts.map((cf, i) => {
                const sv = cf.server;
                const body = cols.map(c => {
                    const a = cf.mine[c.id], b = sv ? sv[c.id] : null;
                    const diff = sv ? String(a ?? '') !== String(b ?? '') && !(typeof a === 'number' && Number(b) === a) : true;
                    // 여러 건이면 다른 칸만 (키 컬럼은 늘)
                    if (many && !diff && c.id !== kc)
                        return '';
                    return `<tr${diff ? ' class="og-cfd"' : ''}><th>${esc(c.def.header ?? c.id)}</th><td>${esc(show(c, a, cf.row))}</td><td>${sv ? esc(show(c, b, cf.row)) : ''}</td></tr>`;
                }).join('');
                const why = `${sv ? this.t('다른 사용자가 먼저 수정했습니다') : this.t('다른 사용자가 삭제했습니다')}${cf.section === 'deleted' ? ' (내가 삭제하려던 행)' : ''}`;
                const pick = many ? `<span class="og-cfxc"><label><input type="radio" name="${nm}-${i}" value="mine" checked> ${mineLabel(cf)}</label><label><input type="radio" name="${nm}-${i}" value="server"> ${this.t('서버 값 받기')}</label></span>` : '';
                return `<div class="og-cfx" data-i="${i}"><div class="og-cfxh"><b>${many ? `${i + 1}. ` : ''}${kc && cf.key != null ? esc(cf.key) : ''}</b> ${why}${pick}</div>
<table><thead><tr><th></th><th>내 값</th><th>서버 값${sv ? '' : ' (삭제됨)'}</th></tr></thead><tbody>${body}</tbody></table></div>`;
            }).join('');
            const foot = many
                ? `<button type="button" class="og-rok" data-a="apply">${this.t('선택대로 적용')}</button><button type="button" data-a="mine">${this.t('모두 내 값')}</button><button type="button" data-a="server">${this.t('모두 서버 값')}</button>`
                : `<button type="button" class="og-rok" data-a="mine">${mineLabel(conflicts[0])}</button><button type="button" data-a="server">${this.t('서버 값 받기')}</button>`;
            setHtml(back, `<div class="og-dlg og-cfl" role="dialog" aria-modal="true" aria-labelledby="${nm}-t"><div class="og-dlgh" id="${nm}-t">저장 충돌 ${conflicts.length}건${many ? ' — 행마다 고르거나 모두 적용' : ''}</div>
<div class="og-cfb">${items}</div>
<div class="og-dlgf">${foot}<button type="button" class="og-rno" data-a="cancel">${this.t('취소')}</button></div></div>`);
            const first = conflicts.find(cf => cf.row >= 0);
            if (first) {
                const vi = this.disp.indexOf(first.row);
                if (vi >= 0)
                    this.setFocus(vi, this.focus.ci, false);
            }
            ;
            back.querySelector('.og-rok').focus();
            const apply = (choice) => {
                this.ds.batch(() => conflicts.forEach((cf, i) => {
                    const ch = choice(i);
                    ch === 'mine' ? res.overwrite++ : res.server++;
                    applyConflict(this.ds, cf, ch, opts);
                    for (const k of [...this.errors.keys()])
                        if (k.startsWith(cf.row + ':'))
                            this.errors.delete(k);
                }));
                finish();
            };
            back.addEventListener('click', e => {
                const b = e.target.closest('button[data-a]');
                if (!b)
                    return;
                const a = b.dataset.a;
                if (a === 'cancel') {
                    res.cancelled = true;
                    finish();
                }
                else if (a === 'mine' || a === 'server')
                    apply(() => a);
                else
                    apply(i => back.querySelector(`input[name="${nm}-${i}"]:checked`)?.value || 'mine');
            });
            back.addEventListener('keydown', e => { e.stopPropagation(); if (e.key === 'Escape') {
                res.cancelled = true;
                finish();
            } });
        });
    }
    clearErrors() { this.errors.clear(); this.asyncErr.clear(); this.asyncVal.clear(); this.rowErrs.clear(); this.warns.clear(); this.asyncWarn.clear(); this.rowWarns.clear(); this.warnAckSig = ''; this.errPanel.hidden = true; this.errPanelKind = null; this.hideTip(); this.bump(); }
    /** @internal */ renderErrPanel(list, kind = 'server') {
        const p = this.errPanel;
        if (!list.length) {
            p.hidden = true;
            this.errPanelKind = null;
            return;
        }
        // 행 사이 검증 목록이 서버 오류 목록을 덮지 않게
        if (kind === 'rows' && this.errPanelKind === 'server' && !p.hidden)
            return;
        this.errPanelKind = kind;
        const esc = (t) => t.replace(/[&<>"]/g, ch => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[ch]));
        const colName = (id) => { const c = id ? this.cols.find(x => x.id === id) : null; return c ? (c.def.header ?? c.id) : (id ?? ''); };
        const item = (e, i) => {
            const vi = e.row >= 0 ? this.disp.indexOf(e.row) : -1;
            return `<li class="og-errl-${e.level ?? 'error'}"><button type="button" data-i="${i}">${vi >= 0 ? ((this.rowNo ? this.rowNo[vi] : vi) + 1) + '행 · ' : ''}${esc(colName(e.col))}: ${esc(e.message)}</button></li>`;
        };
        // 수준별 묶음 (오류 → 경고 → 안내). 오류만 있으면 예전과 같은 한 목록
        const lv = (e) => e.level ?? 'error', onlyErr = list.every(e => lv(e) === 'error');
        const body = onlyErr ? `<ol>${list.map(item).join('')}</ol>` : ['error', 'warning', 'info'].map(L => {
            const idx = list.map((e, i) => (lv(e) === L ? i : -1)).filter(i => i >= 0);
            return idx.length ? `<div class="og-errg og-errg-${L}">${this.t(L === 'error' ? '오류 {n}건' : L === 'warning' ? '경고 {n}건' : '안내 {n}건', { n: idx.length })}</div><ol>${idx.map(i => item(list[i], i)).join('')}</ol>` : '';
        }).join('');
        p.classList.toggle('og-errp-warn', !list.some(e => lv(e) === 'error'));
        setHtml(p, `<div class="og-errh"><b>${kind === 'rows' ? this.t('입력 확인 {n}건', { n: list.length }) : this.t('서버 오류 {n}건', { n: list.length })}</b><button type="button" class="og-errx" aria-label="${this.t('닫기')}">×</button></div>${body}`);
        p.hidden = false;
        p.onclick = ev => {
            const b = ev.target.closest('button');
            if (!b)
                return;
            if (b.classList.contains('og-errx')) {
                p.hidden = true;
                this.errPanelKind = null;
                this.focusGrid();
                return;
            }
            const e = list[+b.dataset.i];
            if (e && e.row >= 0) {
                this.focusCell(e.row, e.col ?? undefined);
                this.focusGrid();
                const m = this.getCellMessage(e.row, e.col ?? '');
                if (m)
                    this.showTip(m.message, m.level);
            }
        };
    }
    /** @internal */ focusNewRow(row) {
        const vi = this.disp.indexOf(row);
        if (vi >= 0)
            this.setFocus(vi, this.focus.ci, false);
    }
    /** 셀 강조 (col 생략하면 행 전체) */
    flashCells(cells, ms = this.opts.flashDuration ?? 900) {
        const until = performance.now() + ms;
        for (const x of cells) {
            if (x.col)
                this.flashUntil.set(x.row + ':' + x.col, until);
            else
                for (const c of this.cols)
                    if (!c.special)
                        this.flashUntil.set(x.row + ':' + c.id, until);
        }
        this.bump();
        clearTimeout(this.flashTimer);
        this.flashTimer = setTimeout(() => { const now = performance.now(); for (const [k, t] of this.flashUntil)
            if (t <= now + 20)
                this.flashUntil.delete(k); this.bump(); if (this.flashUntil.size)
            this.flashCells([], 0); }, ms + 30);
    }
    /** 불러오는 중 표시 (본문 위 반투명 막 + 문구). false 면 끔 */
    setLoading(on) {
        let el = this.root.querySelector(':scope > .og-loading');
        if (!on) {
            el?.remove();
            this.root.removeAttribute('aria-busy');
            return;
        }
        if (!el) {
            el = document.createElement('div');
            el.className = 'og-loading';
            el.setAttribute('role', 'status');
            this.root.appendChild(el);
        }
        el.textContent = typeof on === 'string' ? on : this.t('불러오는 중…');
        this.root.setAttribute('aria-busy', 'true');
    }
    /** 오류 표시 (본문 위 막 + 문구 + "다시 시도"). null 이면 끔 */
    setError(message, retry) {
        let el = this.root.querySelector(':scope > .og-error');
        if (!message) {
            el?.remove();
            return;
        }
        this.setLoading(false);
        if (!el) {
            el = document.createElement('div');
            el.className = 'og-error';
            el.setAttribute('role', 'alert');
            this.root.appendChild(el);
        }
        el.textContent = '';
        const m = document.createElement('div');
        m.className = 'og-errmsg';
        m.textContent = message;
        el.appendChild(m);
        if (retry) {
            const b = document.createElement('button');
            b.type = 'button';
            b.textContent = this.t('다시 시도');
            b.onclick = () => { this.setError(null); retry(); };
            el.appendChild(b);
        }
    }
    /**
     * 불러오기 도우미: 로딩 막 → 끝나면 끔, 실패하면 오류 막(다시 시도 단추). 결과가 비면 emptyText.
     * 예) grid.loadWith(() => fetchColumnar(ds, '/api/labs'))
     */
    async loadWith(task, opts = {}) {
        this.setError(null);
        this.setLoading(opts.loadingText ?? true);
        try {
            const r = await task();
            this.setLoading(false);
            return r;
        }
        catch (e) {
            this.setLoading(false);
            this.setError(opts.errorText?.(e) ?? `불러오지 못했습니다: ${e?.message ?? e}`, () => { this.loadWith(task, opts); });
            return undefined;
        }
    }
    /** 화면 안 확인 창 (window.confirm 대신). 고른 단추 id, 닫으면(Esc) null */
    confirmDialog(title, message, buttons, cls = '') {
        return new Promise(resolve => {
            const back = document.createElement('div');
            back.className = 'og-dlgb og-cfm' + (cls ? ' ' + cls : '');
            const dlg = document.createElement('div');
            dlg.className = 'og-dlg';
            dlg.setAttribute('role', 'alertdialog');
            dlg.setAttribute('aria-modal', 'true');
            const id = 'og-cfm' + nextDlgSeq();
            const h = document.createElement('div');
            h.className = 'og-dlgh';
            h.textContent = title;
            h.id = id + 'h';
            const m = document.createElement('div');
            m.className = 'og-dlgl';
            m.textContent = message;
            m.id = id + 'm';
            dlg.setAttribute('aria-labelledby', h.id);
            dlg.setAttribute('aria-describedby', m.id);
            const f = document.createElement('div');
            f.className = 'og-dlgf';
            const done = (v) => { back.remove(); resolve(v); if (!document.activeElement || document.activeElement === document.body)
                this.focusGrid(); };
            for (const b of buttons) {
                const el = document.createElement('button');
                el.type = 'button';
                el.textContent = b.label;
                el.dataset.id = b.id;
                if (b.primary)
                    el.className = 'og-rok';
                el.onclick = () => done(b.id);
                f.appendChild(el);
            }
            dlg.append(h, m, f);
            back.appendChild(dlg);
            this.root.appendChild(back);
            back.addEventListener('keydown', e => { e.stopPropagation(); if (e.key === 'Escape')
                done(null);
            else if (e.key === 'Tab')
                trapTab(dlg, e); });
            (f.querySelector('.og-rok') ?? f.firstChild)?.focus();
        });
    }
}
